import { PerformanceMonitoringExternalLogCommand } from "../controller/PerformanceMonitoringExternalLogCommand";
import { PerformanceMonitoringTrackingCommand } from "../controller/PerformanceMonitoringTrackingCommand";
import { PerformanceMonitoringEnvTypeEnum } from "../enum/PerformanceMonitoringEnvTypeEnum";
import { PerformanceMonitoringProxy } from "../proxy/PerformanceMonitoringProxy";
import { MeasurementVO } from "../vo/MeasurementVO";

import { NoneValueSignal } from "../../signal/NoneValueSignal";

import { getLogger } from "@goodgamestudios/logger";
import { Timer } from "@goodgamestudios/createjs-utilities";

import DisplayObject = createjs.DisplayObject;
import TimerEvent = createjs.TimerEvent;

const logger = getLogger("CoreJS.PerformanceMonitoring.Service");

/**
 *
 * @param mark
 * @returns {any}
 */
export const measurePerformanceBetweenMarks = (mark: string) => {
    performance.measure(mark, `${mark}_start`, `${mark}_end`);
    // Get all of the measures out.
    // In this case there is only one.
    var measures = performance.getEntriesByName(mark);
    var measure = measures[0];
    logger.debug(`measured duration for ${mark}: ${measure.duration}`);

    // Clean up the stored markers and measures (only for this mark).
    performance.clearMarks(mark);
    performance.clearMeasures(mark);
    return measure;
}

export class PerformanceMonitoringService {

    private static _PerformanceMonitoringService: PerformanceMonitoringService;

    public static getInstance(): PerformanceMonitoringService {
        if (PerformanceMonitoringService._PerformanceMonitoringService == null) {
            PerformanceMonitoringService._PerformanceMonitoringService = new PerformanceMonitoringService();
        }
        return PerformanceMonitoringService._PerformanceMonitoringService;
    }

    private _isInitialized: boolean = false;
    private _isPaused: boolean = false;

    public trackingCommand: PerformanceMonitoringTrackingCommand;
    public externalLogCommand: PerformanceMonitoringExternalLogCommand;

    public performanceMonitoringProxy: PerformanceMonitoringProxy;

    /**
     * Timer handling the interval between each measurment inside a batch
     */
    private _singleMeasurementTimer: Timer;

    /**
     * Cooldown timer between each Measurement batch
     */
    private _coolDownTimerId: number;

    /**
     * Signal dispatched when a current set of measurements is completed.
     */
    public onMeasurementFinished: NoneValueSignal = new NoneValueSignal();

    /**
     * Set of default measurements based on time since the measurements started.
     * 
     * Each MeasurementVO defines:
     * - number of measurements to take.
     * - the interval between each measurement (in seconds).
     * - waiting time till repeating the current set of measurements (in seconds).
     * - running time till the current configuration should run (in seconds).
     * 
     * As an example, this configuration means:
     * <code>new MeasurementVO(5, 2, 20, 10 * 60)</code>
     * 
     * - *6* measurements will be taken (a measurement exactly when the process starts and other 5 measures afterward).
     * - the time interval between each measurement is *2* seconds.
     * - the next set of measurement will happen after waiting *20* seconds.
     * - the current setup will run until *10* minutes of playing time.
     */
    private readonly _defaultMeasurements: MeasurementVO[] = [
        new MeasurementVO(5, 2, 20, 10 * 60),
        new MeasurementVO(5, 4, 40, 30 * 60),
        new MeasurementVO(5, 12, 540, 24 * 60 * 60)
    ];

    private _measurements: MeasurementVO[];
    private _startedAt: number;
    private _currentMeasurementIndex: number;
    private _currentMeasurement: MeasurementVO;

    constructor() {
        if (PerformanceMonitoringService._PerformanceMonitoringService) {
            throw new Error("Calling constructor not allowed! Use getInstance instead.");
        }
    }

    /**
     * initialize the service with for the environment mobile or web
     * @param type
     */
    public init(type: PerformanceMonitoringEnvTypeEnum, view: DisplayObject): void {
        if (!this._isInitialized) {
            //this.view = view;
            this.trackingCommand = new PerformanceMonitoringTrackingCommand();
            this.externalLogCommand = new PerformanceMonitoringExternalLogCommand(type);
            this.performanceMonitoringProxy = new PerformanceMonitoringProxy();
            this._isInitialized = true;
        }
        else {
            logger.debug("PerformanceMonitoringService already initialized");
        }
    }

    /**
     * Starts the performance monitoring system.
     * @param measurements custom definition of measurements to taken
     */
    public startMonitoring(measurements: MeasurementVO[]): void {
        logger.debug(`PerformanceMonitoringService::startMonitoring`);

        // Verify if command should use custom measurement definitions
        if (measurements && measurements.length > 0) {
            this._measurements = measurements;
        } else {
            this._measurements = this._defaultMeasurements;
        }

        // save when measurements started
        this._startedAt = Date.now();

        // reset current measurement
        this._currentMeasurementIndex = 0;
        this._currentMeasurement = this._measurements[this._currentMeasurementIndex];

        // start measuring
        this.startMeasurement();
    }

    /**
     * use this method an logout
     */
    public stopMonitoring(): void {
        logger.debug("PerformanceMonitoringService::STOP MONITORING");
        this.disposeSingleMeasurementTimer();
        clearTimeout(this._coolDownTimerId);
    }

    /**
     * the measurements will be paused, not the timers
     * the measurements will be resumed
     */
    public set pause(value: boolean) {
        if (value == true) {
            logger.debug("PerformanceMonitoringService::measurements paused");
            this.performanceMonitoringProxy.pauseLoginTimeCounter();
        }
        else {
            logger.debug("PerformanceMonitoringService::measurements resumed");
            this.performanceMonitoringProxy.resumeLoginTimeCounter();
        }
        this._isPaused = value;
    }

    //##########################################################################################################
    //These method are related to the singleMeasurementTimer
    //##########################################################################################################

    private startMeasurement(): void {
        logger.debug(`PerformanceMonitoringService::startMeasurement will now collect ${this._currentMeasurement.measurements} measurements (one every ${this._currentMeasurement.intervalBetweenMeasurements} secs)`);

        // clear timers
        clearTimeout(this._coolDownTimerId);
        this.disposeSingleMeasurementTimer();

        // clear current set of collected values
        this.performanceMonitoringProxy.resetMeasurements();

        // trigger immediately first measurement
        this.onSingleMeasurement();

        // setup timer
        this._singleMeasurementTimer = new Timer(this._currentMeasurement.intervalBetweenMeasurements * 1000, this._currentMeasurement.measurements);
        this._singleMeasurementTimer.addEventListener(TimerEvent.TIMER, this.bindFunction(this.onSingleMeasurement));
        this._singleMeasurementTimer.addEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onMeasurementsCompleted));
        this._singleMeasurementTimer.start();
    }

    /**
     * performanceMonitoringProxy will start measuring if it is not paused
     * @param event
     */
    private onSingleMeasurement(event: TimerEvent = null): void {
        if (!this._isPaused) {
            this.performanceMonitoringProxy.measure();
        }
    }

    /**
     * perform tracking and log after collecting the values
     * @param event
     */
    private onMeasurementsCompleted(event: TimerEvent): void {
        logger.debug("PerformanceMonitoringService::FINISHED MONITORING");
        this.onMeasurementFinished.signal();

        // await to take the next measurement based on current measurement settings
        this._coolDownTimerId = window.setTimeout(
            () => {
                // calculate running time since measurements started
                let elapsedTime: number = Date.now() - this._startedAt;

                // check if the current measurement setting ran for the expected time
                if ((elapsedTime > this._currentMeasurement.runUntil * 1000) && (this._currentMeasurementIndex + 1 < this._measurements.length)) {
                    this._currentMeasurementIndex++;
                    this._currentMeasurement = this._measurements[this._currentMeasurementIndex];
                }

                // starts the next measurement
                this.startMeasurement();
            },
            this._currentMeasurement.waitFor * 1000
        );
    }

    /**
     * stops singleMeasurementTimer
     * remove eventlistener
     */
    private disposeSingleMeasurementTimer(): void {
        if (this._singleMeasurementTimer) {
            this._singleMeasurementTimer.stop();
            this._singleMeasurementTimer.removeEventListener(TimerEvent.TIMER, this.onSingleMeasurement);
            this._singleMeasurementTimer.removeEventListener(TimerEvent.TIMER_COMPLETE, this.onMeasurementsCompleted);
            this._singleMeasurementTimer = null;
        }
    }
}
